import React, { useMemo } from 'react'

import {
  Button,
  Checkbox,
  CheckboxGroup,
  CheckboxLabel,
  DataTable,
  DataTableColumn,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@barracuda-internal/bds-core'
import { Block, CheckmarkCircle } from '@barracuda-internal/bds-core/dist/Icons/Core'
import { Warning } from '@barracuda-internal/bds-core/dist/Icons/Feedback'

import inboundSettingsStyles from 'components/pages/inboundSettings/inboundSettingsStyles'
import { useFormatMessage } from 'lib/localization'
import { MessageContentFilter, MessageContentFilterAction, MessageContentFilterMatches } from 'types/Settings'
import { isValidRegex } from 'lib/validation'
import { Cell } from 'components/libs/grid/cell'
import { TableText } from 'components/libs/layout/pages/TableText'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import { useMessageTableLogic, UseMessageTableLogicProps } from './useMessageTableLogic'

const BASE_I18N_KEY = 'ess.inbound_settings.content_policies'
const BASE_INBOUND_I18N_KEY = 'ess.inbound_settings'
const DIALOG_I18N_KEY = 'ess.dialog'

const MessageTable = (props: UseMessageTableLogicProps) => {
  const isReadonly = useIsReadonly()
  const inboundClasses = inboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatDialogText = useFormatMessage(DIALOG_I18N_KEY)
  const formatInboundMessage = useFormatMessage(BASE_INBOUND_I18N_KEY)

  const [state, eventHandlers] = useMessageTableLogic(props)

  return useMemo(
    () => (
      <Grid container>
        <Grid container>
          <Typography variant="h4">{formatMessage('message_content_filter')}</Typography>
          <Button
            className={inboundClasses.bulkEdit}
            onClick={eventHandlers.onBulkEdit}
            variant="contained"
            size="small"
            data-testid="bulk-button"
            disabled={isReadonly}
          >
            {formatInboundMessage('bulk_edit')}
          </Button>
        </Grid>
        <DataTable
          sortable={{
            allowUnsort: false,
            mode: 'single'
          }}
          sort={state.sort}
          onSortChange={eventHandlers.handleSortChange}
          data={state.tableData}
        >
          <DataTableColumn
            sortable
            field="pattern"
            title={formatMessage('pattern')}
            width={250}
            cell={({ dataItem }: { dataItem: MessageContentFilter }) => (
              <Cell data-testid="pattern-cell" className={inboundClasses.smallCell}>
                {!dataItem.id && (
                  <TextField
                    fullWidth
                    name="pattern"
                    defaultValue={state.formState.pattern}
                    inputRef={state.form.fields.pattern.ref}
                    onChange={eventHandlers.handleOnInputChange}
                    inputProps={{ style: { padding: 8 } }}
                    data-testid="new-pattern-field"
                    error={!!state.errors.pattern}
                    helperText={state.errors.pattern ? formatMessage(state.errors.pattern) : ''}
                    disabled={isReadonly}
                  />
                )}
                {dataItem.id && (
                  <Grid container alignItems="center" wrap="nowrap">
                    {!isValidRegex(dataItem.pattern) && (
                      <Tooltip title={formatMessage('invalid_regex_warning')} placement="top">
                        <span style={{ display: 'flex', marginRight: 4 }} data-testid="invalid-regex-warning">
                          <Warning className={inboundClasses.quarantine} />
                        </span>
                      </Tooltip>
                    )}
                    <TableText dataText={dataItem.pattern} truncated dataTestId="pattern-field" />
                  </Grid>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            sortable
            field="action"
            title={formatMessage('action')}
            width={200}
            cell={({ dataItem }: { dataItem: MessageContentFilter }) => (
              <Cell>
                {dataItem.id === '' && (
                  <Select
                    variant="outlined"
                    className={inboundClasses.smallDropdown}
                    name="action"
                    defaultValue={state.formState.action}
                    onChange={eventHandlers.handleOnSelectChange}
                    SelectDisplayProps={{ style: { padding: 8 } }}
                    data-testid="new-action-field"
                    disabled={isReadonly}
                  >
                    {state.actionOptions.map(type => (
                      <MenuItem key={type} value={type}>
                        {formatMessage(type)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                {dataItem.id !== '' && (
                  <Typography variant="body2" data-testid="action-field" className={inboundClasses.flexWrapper}>
                    {dataItem.action === MessageContentFilterAction.BLOCK && <Block className={inboundClasses.block} />}
                    {dataItem.action === MessageContentFilterAction.ALLOW && (
                      <CheckmarkCircle className={inboundClasses.successColor} />
                    )}
                    {dataItem.action === MessageContentFilterAction.QUARANTINE && (
                      <Warning className={inboundClasses.quarantine} />
                    )}
                    {formatMessage(dataItem.action)}
                  </Typography>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            sortable
            field="match"
            title={formatMessage('match')}
            cell={({ dataItem }: { dataItem: MessageContentFilter }) => (
              <Cell className={inboundClasses.smallCell}>
                {!dataItem.id && (
                  <Grid>
                    <CheckboxGroup row>
                      {Object.values(MessageContentFilterMatches).map(value => (
                        <CheckboxLabel
                          control={
                            <Checkbox
                              size="small"
                              name={value}
                              checked={!!state.formState.match[value]}
                              onChange={eventHandlers.handleOnCheckboxChange}
                              disabled={isReadonly}
                            />
                          }
                          label={formatMessage(value)}
                          key={value}
                          data-testid={`checkbox-${value}`}
                        />
                      ))}
                    </CheckboxGroup>
                    <FormHelperText className={inboundClasses.errorColor}>
                      {state.errors.match && formatMessage(state.errors.match)}
                    </FormHelperText>
                  </Grid>
                )}
                {dataItem.id && (
                  <Typography variant="body2" data-testid="match-field">
                    {Object.keys(dataItem.match)
                      .map(key => ` ${formatMessage(key)}`)
                      .toString()}
                  </Typography>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            field="actions"
            title={formatMessage('actions')}
            width={200}
            cell={({ dataItem }: { dataItem: MessageContentFilter }) => (
              <Cell data-testid="action-cell" className={inboundClasses.smallCell}>
                {!dataItem.id && (
                  <Button
                    color="primary"
                    onClick={() => {
                      eventHandlers.onAdd()
                    }}
                    variant="text"
                    data-testid="add-button"
                    disabled={isReadonly}
                  >
                    {formatMessage('add')}
                  </Button>
                )}
                {dataItem.id && (
                  <Button
                    color="primary"
                    onClick={() => {
                      eventHandlers.onRemove(dataItem.id)
                    }}
                    variant="text"
                    data-testid="remove-button"
                    disabled={isReadonly}
                  >
                    {formatMessage('remove')}
                  </Button>
                )}
              </Cell>
            )}
          />
        </DataTable>
        {state.isConfirmDialogOpen && (
          <WarningDialog
            title={formatDialogText('content_policies.title')}
            description={formatDialogText('content_policies.description')}
            onCancel={eventHandlers.onRemoveCancel}
            onConfirm={eventHandlers.onRemoveConfirm}
            cancelButtonLabel={formatDialogText('content_policies.cancel')}
            confirmButtonLabel={formatDialogText('content_policies.confirm')}
          />
        )}
      </Grid>
    ),
    [
      formatMessage,
      formatDialogText,
      inboundClasses.bulkEdit,
      inboundClasses.smallCell,
      inboundClasses.smallDropdown,
      inboundClasses.flexWrapper,
      inboundClasses.block,
      inboundClasses.successColor,
      inboundClasses.quarantine,
      inboundClasses.errorColor,
      eventHandlers,
      isReadonly,
      formatInboundMessage,
      state.sort,
      state.tableData,
      state.isConfirmDialogOpen,
      state.formState.pattern,
      state.formState.action,
      state.formState.match,
      state.form.fields.pattern.ref,
      state.errors.pattern,
      state.errors.match,
      state.actionOptions
    ]
  )
}

export default MessageTable
